/**
 * CategoriaCacheRepository - Repositorio unificado para Categoría Cache
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 */

import { v4 as uuidv4 } from 'uuid';
import { ICategoriaCacheRepository } from '../interfaces/ICategoriaCacheRepository';
import { CategoriaCache, TipoCategoriaCache } from '../../../types/administracion';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { WhereClause } from '../adapters/IDatabaseAdapter';

/**
 * Repositorio unificado de Categoría Cache
 */
export class CategoriaCacheRepository extends BaseRepository<CategoriaCache, string> implements ICategoriaCacheRepository {

  constructor(db: IDatabaseAdapter) {
    super(db, 'categorias_cache', null); // No se sincroniza con Supabase
  }

    async add(data: Omit<CategoriaCache, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();

    const record: CategoriaCache = {
      ...data,
      id,
      uso_count: data.uso_count || 0,
      ultima_fecha_uso: data.ultima_fecha_uso || now
    };

    await this.db.insert('categorias_cache', record);
    return id;
  }

  async update(data: Partial<CategoriaCache> & { id: string }): Promise<void> {
    await this.db.update('categorias_cache', data.id, data);
  }

  async delete(id: string): Promise<void> {
    await this.db.delete('categorias_cache', id);
  }

  async getByTipo(tipo: TipoCategoriaCache): Promise<CategoriaCache[]> {
    return this.db.query<CategoriaCache>('categorias_cache', {
      field: 'tipo',
      operator: 'equals',
      value: tipo
    });
  }

  async getByEmpresa(empresaId: string): Promise<CategoriaCache[]> {
    return this.db.query<CategoriaCache>('categorias_cache', {
      field: 'empresa_id',
      operator: 'equals',
      value: empresaId
    });
  }

  async getByTipoAndEmpresa(tipo: TipoCategoriaCache, empresaId: string): Promise<CategoriaCache[]> {
    return this.db.queryMultiple<CategoriaCache>('categorias_cache', [
      { field: 'tipo', operator: 'equals', value: tipo },
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ]);
  }

  async searchByNombre(nombre: string, tipo?: TipoCategoriaCache): Promise<CategoriaCache[]> {
    const clauses: WhereClause[] = [
      { field: 'nombre', operator: 'includes', value: nombre.toLowerCase() }
    ];

    if (tipo) {
      clauses.push({ field: 'tipo', operator: 'equals', value: tipo });
    }

    return this.db.queryMultiple<CategoriaCache>('categorias_cache', clauses);
  }

    async registrarUso(nombre: string, tipo: TipoCategoriaCache, empresaId: string): Promise<CategoriaCache> {
    const existente = await this.getByNombreExacto(nombre, tipo, empresaId);

    if (existente) {
      const updated = {
        ...existente,
        uso_count: (existente.uso_count || 0) + 1,
        ultima_fecha_uso: this.getCurrentTimestamp()
      };
      await this.db.update('categorias_cache', existente.id, updated);
      return updated;
    }

    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const nuevo: CategoriaCache = {
      id,
      nombre,
      tipo,
      empresa_id: empresaId,
      uso_count: 1,
      ultima_fecha_uso: now
    };

    await this.db.insert('categorias_cache', nuevo);
    return nuevo;
  }

  async getTopByUso(tipo: TipoCategoriaCache, limit: number = 10): Promise<CategoriaCache[]> {
    const todas = await this.getByTipo(tipo);
    return todas
      .sort((a, b) => (b.uso_count || 0) - (a.uso_count || 0))
      .slice(0, limit);
  }

  async getByNombreExacto(nombre: string, tipo: TipoCategoriaCache, empresaId: string): Promise<CategoriaCache | undefined> {
    const resultados = await this.db.queryMultiple<CategoriaCache>('categorias_cache', [
      { field: 'nombre', operator: 'equals', value: nombre },
      { field: 'tipo', operator: 'equals', value: tipo },
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ]);
    return resultados[0];
  }

  async limpiarPocoUsadas(empresaId: string, diasAntiguedad: number, usoCountMinimo: number): Promise<number> {
    const todas = await this.getByEmpresa(empresaId);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - diasAntiguedad);
    const cutoffIso = cutoffDate.toISOString();

    const paraEliminar = todas.filter(c =>
      (c.uso_count || 0) <= usoCountMinimo &&
      c.ultima_fecha_uso < cutoffIso
    );

    const ids = paraEliminar.map(c => c.id);
    await this.db.bulkDelete('categorias_cache', ids);
    return ids.length;
  }

  // ==================== Bulk Operations ====================

  async bulkAdd(items: CategoriaCache[]): Promise<void> {
    await this.db.bulkInsert('categorias_cache', items);
  }

  async bulkAddSilent(items: CategoriaCache[]): Promise<void> {
    await this.db.bulkInsert('categorias_cache', items);
    items.forEach(item => this.emitLocalSync('INSERT', item));
  }

  async bulkPut(items: CategoriaCache[]): Promise<void> {
    await this.db.bulkUpsert('categorias_cache', items);
  }

  async bulkPutSilent(items: CategoriaCache[]): Promise<void> {
    await this.db.bulkUpsert('categorias_cache', items);
    items.forEach(item => this.emitLocalSync('UPDATE', item));
  }

  async bulkDelete(ids: string[]): Promise<void> {
    await this.db.bulkDelete('categorias_cache', ids);
  }

  async bulkDeleteSilent(ids: string[]): Promise<void> {
    await this.db.bulkDelete('categorias_cache', ids);
    ids.forEach(id => this.emitLocalSync('DELETE', id));
  }

  async getLastByDate(dateColumn: string = 'ultima_fecha_uso'): Promise<CategoriaCache | undefined> {
    const results = await this.db.query<CategoriaCache>('categorias_cache', undefined, {
      column: dateColumn,
      direction: 'desc'
    });
    return results[0] || undefined;
  }
}
